import React, { useState } from 'react';
import styled from '@emotion/styled';
import FormField from './FormField';
import TextareaWithLineBreaks from './TextareaWithLineBreaks';

const FormContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(9, 30, 66, 0.15), 0 0 1px rgba(9, 30, 66, 0.31);
  padding: 32px;
  height: fit-content;
  position: relative;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.3));
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
`;

const FormTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 28px;
  font-weight: 600;
  color: #172B4D;
  margin: 0 0 32px;
  letter-spacing: -0.01em;
`;

const FormTitleIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: #DEEBFF;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0052CC;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    background-color: #B3D4FF;
  }
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 20px;
  font-size: 16px;
  color: #172B4D;
  font-weight: 600;
  display: flex;
  align-items: center;
  
  &:before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 16px;
    background-color: #0052CC;
    margin-right: 8px;
    border-radius: 2px;
  }
`;

const HighlightSection = styled.div`
  border: 1px solid #DFE1E6;
  border-radius: 6px;
  padding: 20px;
  margin-bottom: 24px;
  background-color: #FAFBFC;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const HighlightTitle = styled.h4`
  margin-bottom: 16px;
  font-size: 15px;
  color: #172B4D;
  font-weight: 600;
`;

const FileInputLabel = styled.label`
  display: block;
  margin-bottom: 10px;
  font-weight: 500;
  font-size: 14px;
  color: #172B4D;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputButton = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: white;
  border: 2px solid #DFE1E6;
  border-radius: 3px;
  color: #42526E;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #FAFBFC;
    border-color: #C1C7D0;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ImageSizeHelper = styled.div`
  font-size: 12px;
  color: #6B778C;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const FilePreview = styled.div`
  margin-top: 12px;
  
  img {
    max-width: 200px;
    max-height: 200px;
    border-radius: 3px;
    border: 1px solid #DFE1E6;
  }
`;

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 8px;
  background: none;
  border: none;
  color: #DE350B;
  font-size: 12px;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: white;
  border: 2px solid #DFE1E6;
  border-radius: 3px;
  color: #42526E;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  input:checked + & {
    background-color: #DEEBFF;
    border-color: #0052CC;
    color: #0052CC;
  }
  
  &:hover {
    background-color: #FAFBFC;
    border-color: #C1C7D0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  position: relative;
  width: 100%;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #172B4D;
`;

const CharacterCount = styled.div`
  font-size: 12px;
  color: ${props => {
    if (props.count > props.max) return '#FF5630';
    if (props.count >= props.max * 0.9) return '#FFAB00';
    return '#6B778C';
  }};
  text-align: right;
  margin-top: 6px;
  font-weight: ${props => props.count >= props.max * 0.9 ? '500' : 'normal'};
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #6B778C;
  margin-top: 6px;
  line-height: 1.4;
`;

const FieldFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 6px;
  width: 100%;
`;

const ErrorMessage = styled.div`
  color: #DE350B;
  font-size: 14px;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #FFEBE6;
  border-radius: 3px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background-color: #DFE1E6;
  }
  
  span {
    padding: 0 16px;
    background-color: white;
    position: relative;
    z-index: 1;
    color: #6B778C;
    font-size: 14px;
    font-weight: 500;
  }
`;

const MarketplaceForm = ({ formData, onChange }) => {
  const [fileError, setFileError] = useState(null);
  const [inputError, setInputError] = useState(null);

  const validateInput = (name, value) => {
    // Reset error state
    setInputError(null);
    
    // Basic XSS prevention - check for suspicious patterns
    const suspiciousPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript\s*:/gi,
      /<[^>]*\s+on\w+\s*=/gi,
      /data\s*:[^;]*;base64/gi
    ];
    
    if (typeof value === 'string') {
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(value)) {
          console.warn(`Input validation failed: ${name} contains potentially malicious content`);
          setInputError(`Invalid input detected. Please remove any code or script elements.`);
          return false;
        }
      }
    }

    return true;
  };

  const handleInputChange = (name, value) => {
    // Truncate to max length instead of rejecting
    const maxLengths = {
      appName: 60,
      appTagline: 130,
      companyName: 40,
      highlightTitle1: 50,
      highlightTitle2: 50,
      highlightTitle3: 50,
      highlightSummary1: 220,
      highlightSummary2: 220,
      highlightSummary3: 220,
      moreDetails: 1000,
      appYouTubeUrl: 200,
      videoUrl: 200
    };
    if (maxLengths[name] && value.length > maxLengths[name]) {
      value = value.substring(0, maxLengths[name]);
    }

    if (validateInput(name, value)) {
      // For moreDetails, ensure line breaks and apostrophes are preserved
      if (name === 'moreDetails') {
        // Replace any \r\n with \n for consistency and decode HTML entities
        const normalizedValue = value
          .replace(/\r\n/g, '\n')
          .replace(/&#039;/g, "'")
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>');
        
        onChange({
          ...formData,
          [name]: normalizedValue
        });
      } else {
        onChange({
          ...formData,
          [name]: value
        });
      }
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    // Clear any previous error
    setFileError(null);
    
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type and size
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      
      if (!validTypes.includes(file.type)) {
        console.warn(`File validation failed: ${name} has invalid type ${file.type}. Only PNG, JPG, and GIF are allowed.`);
        setFileError("Please upload only PNG, JPG, or GIF images.");
        return; // Silently reject invalid file type
      }
      
      if (file.size > maxSize) {
        console.warn(`File validation failed: ${name} exceeds maximum size of 5MB (${(file.size / (1024 * 1024)).toFixed(2)}MB).`);
        setFileError("That file is too large! Please upload an image that is 5MB or smaller.");
        return; // Reject files larger than 5MB with message
      }
      
      // Proceed with valid file
      const reader = new FileReader();
      reader.onload = (event) => {
        onChange({
          ...formData,
          [name]: event.target.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = (fieldName) => {
    onChange({
      ...formData,
      [fieldName]: null
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    onChange({
      ...formData,
      worksWithProducts: {
        ...formData.worksWithProducts,
        [name]: checked
      }
    });
  };

  const handleFileButtonClick = (inputRef) => {
    inputRef.current.click();
  };

  const logoInputRef = React.useRef(null);
  const bannerInputRef = React.useRef(null);
  const screenshot1InputRef = React.useRef(null);
  const screenshot2InputRef = React.useRef(null);
  const screenshot3InputRef = React.useRef(null);
  const supportingMedia1InputRef = React.useRef(null);
  const supportingMedia2InputRef = React.useRef(null);
  const supportingMedia3InputRef = React.useRef(null);
  const supportingMedia4InputRef = React.useRef(null);
  const supportingMedia5InputRef = React.useRef(null);

  return (
    <FormContainer>
      <FormTitle>
        <FormTitleIcon>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </FormTitleIcon>
        Marketplace Listing Builder
      </FormTitle>
      
      {inputError && (
        <ErrorMessage style={{ marginBottom: '20px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {inputError}
        </ErrorMessage>
      )}
      
      <form>
        <FormSection>
          <SectionTitle>Basic Information</SectionTitle>
          <FormGroup>
            <FormField
              label="App Name"
              name="appName"
              value={formData.appName}
              onChange={handleInputChange}
              maxLength={60}
              required
              placeholder="Enter a clear, memorable name that reflects your app's purpose"
              helperText="Example: TaskFlow for Jira (Task Management & Automation)"
            />
          </FormGroup>
          <FormGroup>
            <FormField
              label="Company Name"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              maxLength={40}
              required
              placeholder="Enter your company name (max 40 characters)"
              helperText="Example: TaskFlow Solutions"
            />
          </FormGroup>
          <FormGroup>
            <FileInputLabel>App Logo</FileInputLabel>
            <FileInputButton onClick={() => handleFileButtonClick(logoInputRef)}>
              <FileInput
                ref={logoInputRef}
                type="file"
                name="appLogo"
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/jpg,image/gif"
              />
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              Upload Logo
            </FileInputButton>
            {fileError && (
              <ErrorMessage>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {fileError}
              </ErrorMessage>
            )}
            {formData.appLogo && (
              <RemoveButton
                type="button"
                onClick={() => handleDeleteImage('appLogo')}
              >
                Remove
              </RemoveButton>
            )}
            <ImageSizeHelper>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              Recommended size: 72 x 72px (PNG or JPG, max 1MB)
            </ImageSizeHelper>
          </FormGroup>
          <FormGroup>
            <FormField
              label="App Tagline"
              name="appTagline"
              value={formData.appTagline}
              onChange={handleInputChange}
              maxLength={130}
              required
              placeholder="Enter a catchy tagline that describes your app (max 130 characters)"
              helperText="Example: Streamline your team's workflow with intelligent task management, automated processes, and real-time collaboration tools"
            />
          </FormGroup>
          <FormGroup>
            <FileInputLabel>App Banner</FileInputLabel>
            <FileInputButton onClick={() => handleFileButtonClick(bannerInputRef)}>
              <FileInput
                ref={bannerInputRef}
                type="file"
                name="appBanner"
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/jpg,image/gif"
              />
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              Upload Banner
            </FileInputButton>
            {fileError && (
              <ErrorMessage>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {fileError}
              </ErrorMessage>
            )}
            {formData.appBanner && (
              <RemoveButton
                type="button"
                onClick={() => handleDeleteImage('appBanner')}
              >
                Remove
              </RemoveButton>
            )}
            <ImageSizeHelper>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              Recommended size: 1120 x 548px (PNG or JPG, max 5MB)
            </ImageSizeHelper>
            
            <OrDivider>
              <span>OR</span>
            </OrDivider>
            
            <FormField
              label="YouTube Video URL"
              name="appYouTubeUrl"
              value={formData.appYouTubeUrl}
              onChange={handleInputChange}
              maxLength={200}
              placeholder="https://www.youtube.com/watch?v=..."
              helperText={
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                    <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                  </svg>
                  A YouTube video will be displayed prominently in your listing
                </span>
              }
            />
          </FormGroup>
        </FormSection>

        <FormSection>
          <SectionTitle>Compatibility</SectionTitle>
          <CheckboxGroup>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="jiraCloud"
                checked={formData.worksWithProducts.jiraCloud}
                onChange={handleCheckboxChange}
              />
              Jira Cloud
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="jiraDataCenter"
                checked={formData.worksWithProducts.jiraDataCenter}
                onChange={handleCheckboxChange}
              />
              Jira Data Center
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="confluenceCloud"
                checked={formData.worksWithProducts.confluenceCloud}
                onChange={handleCheckboxChange}
              />
              Confluence Cloud
            </CheckboxLabel>
            <CheckboxLabel>
              <input
                type="checkbox"
                name="confluenceDataCenter"
                checked={formData.worksWithProducts.confluenceDataCenter}
                onChange={handleCheckboxChange}
              />
              Confluence Data Center
            </CheckboxLabel>
          </CheckboxGroup>
        </FormSection>

        <FormSection>
          <SectionTitle>App Highlights</SectionTitle>
          <HighlightSection>
            <HighlightTitle>Highlight 1</HighlightTitle>
            <FormGroup>
              <FormField
                label="Title"
                name="highlightTitle1"
                value={formData.highlightTitle1}
                onChange={handleInputChange}
                required
                maxLength={50}
                placeholder="Enter a title for your first highlight"
                helperText="Example: Smart Task Management & Automation"
              />
            </FormGroup>
            <FormGroup>
              <FormField
                label="Summary"
                name="highlightSummary1"
                value={formData.highlightSummary1}
                onChange={handleInputChange}
                type="textarea"
                required
                placeholder="Describe your first highlight feature in detail"
                helperText="Format your content for better readability:
• Press Enter twice for new paragraphs
• Start lines with * or - for bullet points
• Keep descriptions clear and concise (max 220 characters)"
                textarea={true}
                maxLength={220}
              />
            </FormGroup>
            <FormGroup>
              <FileInputLabel>Screenshot</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(screenshot1InputRef)}>
                <FileInput
                  ref={screenshot1InputRef}
                  type="file"
                  name="highlightScreenshot1"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Screenshot
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.highlightScreenshot1 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('highlightScreenshot1')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          </HighlightSection>

          <HighlightSection>
            <HighlightTitle>Highlight 2</HighlightTitle>
            <FormGroup>
              <FormField
                label="Title"
                name="highlightTitle2"
                value={formData.highlightTitle2}
                onChange={handleInputChange}
                required
                maxLength={50}
                placeholder="Enter a title for your second highlight"
                helperText="Example: Real-time Team Collaboration"
              />
            </FormGroup>
            <FormGroup>
              <FormField
                label="Summary"
                name="highlightSummary2"
                value={formData.highlightSummary2}
                onChange={handleInputChange}
                type="textarea"
                required
                placeholder="Describe your second highlight feature in detail"
                helperText="Format your content for better readability:
• Press Enter twice for new paragraphs
• Start lines with * or - for bullet points
• Keep descriptions clear and concise (max 220 characters)"
                textarea={true}
                maxLength={220}
              />
            </FormGroup>
            <FormGroup>
              <FileInputLabel>Screenshot</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(screenshot2InputRef)}>
                <FileInput
                  ref={screenshot2InputRef}
                  type="file"
                  name="highlightScreenshot2"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Screenshot
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.highlightScreenshot2 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('highlightScreenshot2')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          </HighlightSection>

          <HighlightSection>
            <HighlightTitle>Highlight 3</HighlightTitle>
            <FormGroup>
              <FormField
                label="Title"
                name="highlightTitle3"
                value={formData.highlightTitle3}
                onChange={handleInputChange}
                required
                maxLength={50}
                placeholder="Enter a title for your third highlight"
                helperText="Example: Advanced Analytics & Reporting"
              />
            </FormGroup>
            <FormGroup>
              <FormField
                label="Summary"
                name="highlightSummary3"
                value={formData.highlightSummary3}
                onChange={handleInputChange}
                type="textarea"
                required
                placeholder="Describe your third highlight feature in detail"
                helperText="Format your content for better readability:
• Press Enter twice for new paragraphs
• Start lines with * or - for bullet points
• Keep descriptions clear and concise (max 220 characters)"
                textarea={true}
                maxLength={220}
              />
            </FormGroup>
            <FormGroup>
              <FileInputLabel>Screenshot</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(screenshot3InputRef)}>
                <FileInput
                  ref={screenshot3InputRef}
                  type="file"
                  name="highlightScreenshot3"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Screenshot
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.highlightScreenshot3 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('highlightScreenshot3')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          </HighlightSection>
        </FormSection>

        <FormSection>
          <SectionTitle>Supporting Media</SectionTitle>
          <p style={{ marginBottom: '16px', color: '#6B778C', fontSize: '14px' }}>
            Add up to 5 additional images to showcase your app's features and interface
          </p>
          
          <FormGroup>
            <FileInputLabel>Supporting Image 1</FileInputLabel>
            <FileInputButton onClick={() => handleFileButtonClick(supportingMedia1InputRef)}>
              <FileInput
                ref={supportingMedia1InputRef}
                type="file"
                name="supportingMedia1"
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/jpg,image/gif"
              />
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              Upload Image 1
            </FileInputButton>
            {fileError && (
              <ErrorMessage>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {fileError}
              </ErrorMessage>
            )}
            {formData.supportingMedia1 && (
              <RemoveButton
                type="button"
                onClick={() => handleDeleteImage('supportingMedia1')}
              >
                Remove
              </RemoveButton>
            )}
            <ImageSizeHelper>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 8v4M12 16h.01"/>
              </svg>
              Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
            </ImageSizeHelper>
          </FormGroup>
          
          {formData.supportingMedia1 && (
            <FormGroup>
              <FileInputLabel>Supporting Image 2</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(supportingMedia2InputRef)}>
                <FileInput
                  ref={supportingMedia2InputRef}
                  type="file"
                  name="supportingMedia2"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Image 2
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.supportingMedia2 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('supportingMedia2')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          )}
          
          {formData.supportingMedia2 && (
            <FormGroup>
              <FileInputLabel>Supporting Image 3</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(supportingMedia3InputRef)}>
                <FileInput
                  ref={supportingMedia3InputRef}
                  type="file"
                  name="supportingMedia3"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Image 3
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.supportingMedia3 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('supportingMedia3')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          )}
          
          {formData.supportingMedia3 && (
            <FormGroup>
              <FileInputLabel>Supporting Image 4</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(supportingMedia4InputRef)}>
                <FileInput
                  ref={supportingMedia4InputRef}
                  type="file"
                  name="supportingMedia4"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Image 4
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.supportingMedia4 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('supportingMedia4')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          )}
          
          {formData.supportingMedia4 && (
            <FormGroup>
              <FileInputLabel>Supporting Image 5</FileInputLabel>
              <FileInputButton onClick={() => handleFileButtonClick(supportingMedia5InputRef)}>
                <FileInput
                  ref={supportingMedia5InputRef}
                  type="file"
                  name="supportingMedia5"
                  onChange={handleFileChange}
                  accept="image/png,image/jpeg,image/jpg,image/gif"
                />
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
                Upload Image 5
              </FileInputButton>
              {fileError && (
                <ErrorMessage>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  {fileError}
                </ErrorMessage>
              )}
              {formData.supportingMedia5 && (
                <RemoveButton
                  type="button"
                  onClick={() => handleDeleteImage('supportingMedia5')}
                >
                  Remove
                </RemoveButton>
              )}
              <ImageSizeHelper>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4M12 16h.01"/>
                </svg>
                Recommended size: 1840 x 900px (PNG or JPG, max 5MB)
              </ImageSizeHelper>
            </FormGroup>
          )}
        </FormSection>

        <FormSection>
          <SectionTitle>Additional Details</SectionTitle>
          <div style={{ 
            backgroundColor: '#F4F5F7', 
            padding: '20px', 
            borderRadius: '8px',
            marginBottom: '24px'
          }}>
            <FormGroup>
              <FormLabel htmlFor="moreDetails">
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  More Details
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#0052CC', 
                    fontWeight: 'normal',
                    padding: '2px 6px',
                    backgroundColor: '#DEEBFF',
                    borderRadius: '3px'
                  }}>
                    Multi-paragraph
                  </span>
                </span>
              </FormLabel>
              <TextareaWithLineBreaks
                id="moreDetails"
                name="moreDetails"
                value={formData.moreDetails}
                onChange={handleInputChange}
                maxLength={1000}
                isWarning={formData.moreDetails.length >= 900 && formData.moreDetails.length < 1000}
                isError={formData.moreDetails.length >= 1000}
                placeholder={`Describe your app's features and benefits in detail. For example:

Our app streamlines task management with powerful features:

* Real-time collaboration across teams
* Customizable workflows and automation
* Advanced reporting and analytics
* Integration with popular tools

The intuitive interface makes it easy to:
* Create and assign tasks quickly
* Track progress with visual dashboards
* Generate detailed performance reports

Start boosting your team's productivity today!`}
              />
              <FieldFooter>
                <HelperText>
                  Format your content for better readability:
                  • Press Enter twice for new paragraphs
                  • Start lines with * or - for bullet points
                  • Keep descriptions clear and concise
                </HelperText>
                <CharacterCount 
                  count={formData.moreDetails.length} 
                  max={1000}
                >
                  {formData.moreDetails.length}/1000
                </CharacterCount>
              </FieldFooter>
            </FormGroup>
          </div>
          <FormGroup>
            <FormField
              label="YouTube Video URL"
              name="videoUrl"
              value={formData.videoUrl}
              onChange={handleInputChange}
              type="url"
              maxLength={200}
              placeholder="Enter a YouTube video URL to showcase your app"
              helperText="Example: https://www.youtube.com/watch?v=your-video-id"
            />
          </FormGroup>
        </FormSection>
      </form>
    </FormContainer>
  );
};

export default MarketplaceForm; 